import { AbstractMentionResource, type MentionProvider } from '@atlaskit/mention/resource';
import {
	type MentionDescription,
	type MentionsResult,
	type MentionStats,
} from '@atlaskit/mention/types';
import { type Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import { agentToMentionDescription } from '../../utils/agent-to-mention-description';

const MAX_RECOMMENDED_AGENT_RESULTS = 5;

/** Getter for the current recommended-agent list. Function form so consumers can plumb via a ref. */
type RecommendedAgentsGetter = () => readonly Agent[];

/** Minimal shape passed back when the user picks an agent row. */
export type SelectedAgentRef = {
	id: string;
	name: string;
};

/** Fired when the user picks an agent row in the typeahead. */
type OnAgentSelected = (agent: SelectedAgentRef) => void;

type WithAgentSupportOptions = {
	/** Agents to prepend on empty-query (preloaded from URS recommendations). */
	getRecommendedAgents: RecommendedAgentsGetter;
	/** Fires when the user picks an agent row. */
	onAgentSelected?: OnAgentSelected;
};

/** Internal key used for the wrapper's subscription on the inner provider. */
const INNER_SUBSCRIPTION_KEY = 'rovo-agent-mention-decorator';

/**
 * Factory that decorates a `MentionProvider` so it surfaces Rovo agents in
 * the @-mention typeahead alongside the people the inner provider already
 * returns.
 *
 * - **Empty query**: prepends the consumer-supplied recommended agents to
 *   whatever the inner provider emits (which is people-only for empty
 *   queries from URS).
 * - **Typed query**: relies on the inner provider to return agents from
 *   URS in the same response as people. Agent rows are discriminated by
 *   `appType === 'agent'`. No client-side overlay or second round-trip.
 *
 * The decorator composes the inner provider rather than extending it so the
 * inner retains full ownership of people search, debouncing, and analytics.
 *
 * Consumers MUST call `.dispose()` on the returned instance when discarding
 * the wrapper (e.g. from a `useEffect` cleanup) so the inner provider
 * releases its reference to the wrapper's subscription callback. Prefer
 * `useChatMentionResource` which owns this lifecycle for you.
 */
export const withAgentSupport = (
	innerProvider: MentionProvider,
	options: WithAgentSupportOptions,
): AgentAwareMentionProvider => new AgentAwareMentionProvider(innerProvider, options);

/**
 * Mention provider that merges Rovo agents into typeahead results. Returned
 * by `withAgentSupport`. Implements `MentionProvider` so it can be passed
 * directly to the editor; adds `dispose()` for explicit cleanup of the
 * subscription it holds on the inner provider.
 */
export class AgentAwareMentionProvider extends AbstractMentionResource {
	private readonly innerProvider: MentionProvider;
	private readonly getRecommendedAgents: RecommendedAgentsGetter;
	private readonly onAgentSelected?: OnAgentSelected;

	constructor(innerProvider: MentionProvider, options: WithAgentSupportOptions) {
		super();
		this.innerProvider = innerProvider;
		this.getRecommendedAgents = options.getRecommendedAgents;
		this.onAgentSelected = options.onAgentSelected;

		this.innerProvider.subscribe(
			INNER_SUBSCRIPTION_KEY,
			(mentions, query, stats) => this.emitMerged(mentions, query, stats),
			undefined,
			undefined,
			undefined,
			undefined,
		);
	}

	/**
	 * Releases this wrapper's subscription on the inner provider. Call once
	 * before discarding the wrapper instance so the inner provider can drop
	 * its reference to the closure-bound callback.
	 */
	dispose(): void {
		this.innerProvider.unsubscribe(INNER_SUBSCRIPTION_KEY);
	}

	filter(query?: string): void {
		this.innerProvider.filter(query);
	}

	isFiltering(query: string): boolean {
		return this.innerProvider.isFiltering(query);
	}

	shouldHighlightMention(mention: MentionDescription): boolean {
		return this.innerProvider.shouldHighlightMention?.(mention) ?? false;
	}

	recordMentionSelection(mention: MentionDescription): void {
		// Always notify the inner provider so people-row analytics / recent-pick keep working.
		this.innerProvider.recordMentionSelection?.(mention);

		if (!isAgentMention(mention) || !this.onAgentSelected) {
			return;
		}
		this.onAgentSelected({ id: mention.id, name: mention.name ?? '' });
	}

	private emitMerged(
		innerMentions: MentionDescription[],
		query: string | undefined,
		stats: MentionStats | undefined,
	): void {
		const trimmedQuery = (query ?? '').trim();

		// Empty query: prepend recommended agents; inner provider returns people only.
		// Typed query: inner provider already returns agents (via appType) + people; pass through.
		const prefix =
			trimmedQuery.length === 0
				? this.getRecommendedAgents()
						.slice(0, MAX_RECOMMENDED_AGENT_RESULTS)
						.map(agentToMentionDescription)
				: [];

		const merged: MentionsResult = {
			mentions: [...prefix, ...innerMentions],
			query: query ?? '',
		};

		this._notifyListeners(merged, stats);
	}
}

const isAgentMention = (mention: MentionDescription): boolean =>
	mention.userType === 'APP' && mention.appType === 'agent';
